/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  createWorketFromSrc,
  registeredWorklets,
} from "./audioworklet-registry";

export class AudioStreamer {
  private sampleRate: number = 24000;
  private bufferSize: number = 7680;
  // A queue of audio buffers to be played. Each buffer is a Float32Array.
  private audioQueue: Float32Array[] = [];
  private isPlaying: boolean = false;
  // Indicates if the stream has finished playing, e.g., interrupted.
  private isStreamComplete: boolean = false;
  private checkInterval: number | null = null;
  private scheduledTime: number = 0;
  private initialBufferTime: number = 0.1; //0.1 // 100ms initial buffer
  // Web Audio API nodes. source => gain => destination
  public gainNode: GainNode;
  public source: AudioBufferSourceNode;
  private endOfQueueAudioSource: AudioBufferSourceNode | null = null;

  public onComplete = () => {};

  // iOS Safari's echo canceller ignores audio played straight to WebAudio's
  // context.destination — the phone mic then hears JARVIS verbatim and the
  // server reads it as operator barge-in (brief transcript 2026-07-10 L150).
  // Routing playback through a MediaStream-backed <audio> element puts it on
  // the system output path the AEC uses as its reference signal.
  private outputDest: MediaStreamAudioDestinationNode | null = null;
  private audioEl: HTMLAudioElement | null = null;
  private playoutArmed = false;

  constructor(public context: AudioContext) {
    this.gainNode = this.context.createGain();
    this.source = this.context.createBufferSource();
    // The element reroute is a /brief echo fix ONLY — the tablet/kid surfaces
    // keep the original direct playout untouched (operator 2026-07-10:
    // "the tablet build is perfect... preserve it at all costs").
    const briefSurface = typeof window !== "undefined" && /^\/brief/.test((window.location && window.location.pathname) || "");
    if (!briefSurface) {
      this.gainNode.connect(this.context.destination);
      this.addPCM16 = this.addPCM16.bind(this);
      return;
    }
    try {
      this.outputDest = this.context.createMediaStreamDestination();
      this.audioEl = new Audio();
      this.audioEl.srcObject = this.outputDest.stream;
      (this.audioEl as any).playsInline = true;
      this.audioEl.autoplay = true;
      this.audioEl.style.display = "none";
      if (typeof document !== "undefined" && document.body) document.body.appendChild(this.audioEl);
      this.gainNode.connect(this.outputDest);
      (window as any).__briefPlayoutPath = "element-unarmed";
      // Element playback must start inside a user gesture on iOS — the Begin
      // tap (any tap) arms it. Until armed, addPCM16 keeps retrying/falls back.
      if (typeof document !== "undefined") {
        document.addEventListener("pointerup", this.armPlayout, true);
        document.addEventListener("touchend", this.armPlayout, true);
      }
    } catch {
      this.outputDest = null;
      this.audioEl = null;
      this.gainNode.connect(this.context.destination);
      (window as any).__briefPlayoutPath = "direct-init-failed";
    }
    this.addPCM16 = this.addPCM16.bind(this);
  }

  private armPlayout = () => {
    if (!this.audioEl || this.playoutArmed) return;
    this.audioEl.play().then(() => {
      this.playoutArmed = true;
      (window as any).__briefPlayoutPath = "element";
      if (typeof document !== "undefined") {
        document.removeEventListener("pointerup", this.armPlayout, true);
        document.removeEventListener("touchend", this.armPlayout, true);
      }
    }).catch(() => {});
  };

  private playoutSink(): AudioNode {
    return this.outputDest ?? this.context.destination;
  }

  // Autoplay refusal or a dead element must never silence JARVIS: rewire the
  // gain straight to WebAudio playout (the pre-fix path) and drop the element.
  private fallbackToDirectPlayout() {
    if (!this.outputDest) return;
    try { this.gainNode.disconnect(this.outputDest); } catch {}
    try { this.audioEl?.pause(); if (this.audioEl) this.audioEl.srcObject = null; } catch {}
    this.outputDest = null;
    this.audioEl = null;
    this.gainNode.connect(this.context.destination);
    (window as any).__briefPlayoutPath = "direct-fallback";
  }

  async addWorklet<T extends (d: any) => void>(
    workletName: string,
    workletSrc: string,
    handler: T
  ): Promise<this> {
    let workletsRecord = registeredWorklets.get(this.context);
    if (workletsRecord && workletsRecord[workletName]) {
      // the worklet already exists on this context
      // add the new handler to it
      workletsRecord[workletName].handlers.push(handler);
      return Promise.resolve(this);
      //throw new Error(`Worklet ${workletName} already exists on context`);
    }

    if (!workletsRecord) {
      registeredWorklets.set(this.context, {});
      workletsRecord = registeredWorklets.get(this.context)!;
    }

    // create new record to fill in as becomes available
    workletsRecord[workletName] = { handlers: [handler] };

    const src = createWorketFromSrc(workletName, workletSrc);
    await this.context.audioWorklet.addModule(src);
    const worklet = new AudioWorkletNode(this.context, workletName);

    //add the node into the map
    workletsRecord[workletName].node = worklet;

    return this;
  }

  /**
   * Converts a Uint8Array of PCM16 audio data into a Float32Array.
   * PCM16 is a common raw audio format, but the Web Audio API generally
   * expects audio data as Float32Arrays with samples normalized between -1.0 and 1.0.
   * This function handles that conversion.
   * @param chunk The Uint8Array containing PCM16 audio data.
   * @returns A Float32Array representing the converted audio data.
   */
  private _processPCM16Chunk(chunk: Uint8Array): Float32Array {
    const float32Array = new Float32Array(chunk.length / 2);
    const dataView = new DataView(chunk.buffer);

    for (let i = 0; i < chunk.length / 2; i++) {
      try {
        const int16 = dataView.getInt16(i * 2, true);
        float32Array[i] = int16 / 32768;
      } catch (e) {
        console.error(e);
      }
    }
    return float32Array;
  }

  addPCM16(chunk: Uint8Array) {
    // Element playout should be armed by the Begin tap; if a chunk arrives
    // first, try once more outside a gesture, and on refusal fall back to
    // direct playout so the voice never dies (echo is the lesser failure).
    if (this.audioEl && !this.playoutArmed) {
      this.audioEl.play().then(() => { this.playoutArmed = true; (window as any).__briefPlayoutPath = "element"; })
        .catch(() => this.fallbackToDirectPlayout());
    }
    // Reset the stream complete flag when a new chunk is added.
    this.isStreamComplete = false;
    // Process the chunk into a Float32Array
    let processingBuffer = this._processPCM16Chunk(chunk);
    // Add the processed buffer to the queue if it's larger than the buffer size.
    // This is to ensure that the buffer is not too large.
    while (processingBuffer.length >= this.bufferSize) {
      const buffer = processingBuffer.slice(0, this.bufferSize);
      this.audioQueue.push(buffer);
      processingBuffer = processingBuffer.slice(this.bufferSize);
    }
    // Add the remaining buffer to the queue if it's not empty.
    if (processingBuffer.length > 0) {
      this.audioQueue.push(processingBuffer);
    }
    // Start playing if not already playing.
    if (!this.isPlaying) {
      this.isPlaying = true;
      // Initialize scheduledTime only when we start playing
      this.scheduledTime = this.context.currentTime + this.initialBufferTime;
      this.scheduleNextBuffer();
    } else {
      // Schedule new arrivals IMMEDIATELY — never wait for the re-arm timer.
      // Background tabs (operator reading a link in Safari while the voice
      // keeps going) clamp timers to ~1s, which turned playback into 0.2s
      // bursts with dead air between (2026-07-03). WebAudio plays whatever is
      // already scheduled regardless of timer throttling.
      this.scheduleNextBuffer();
    }
  }

  private createAudioBuffer(audioData: Float32Array): AudioBuffer {
    const audioBuffer = this.context.createBuffer(
      1,
      audioData.length,
      this.sampleRate
    );
    audioBuffer.getChannelData(0).set(audioData);
    return audioBuffer;
  }

  private scheduleNextBuffer() {
    // Schedule EVERYTHING queued, not a 0.2s window: throttled background-tab
    // timers can't create gaps in audio that is already on the WebAudio clock.
    // Interruption stays safe — stop() mutes via a gain ramp, not the queue.
    while (this.audioQueue.length > 0) {
      const audioData = this.audioQueue.shift()!;
      const audioBuffer = this.createAudioBuffer(audioData);
      const source = this.context.createBufferSource();

      if (this.audioQueue.length === 0) {
        if (this.endOfQueueAudioSource) {
          this.endOfQueueAudioSource.onended = null;
        }
        this.endOfQueueAudioSource = source;
        source.onended = () => {
          if (
            !this.audioQueue.length &&
            this.endOfQueueAudioSource === source
          ) {
            this.endOfQueueAudioSource = null;
            this.onComplete();
          }
        };
      }

      source.buffer = audioBuffer;
      source.connect(this.gainNode);

      const worklets = registeredWorklets.get(this.context);

      if (worklets) {
        Object.entries(worklets).forEach(([workletName, graph]) => {
          const { node, handlers } = graph;
          if (node) {
            source.connect(node);
            node.port.onmessage = function (ev: MessageEvent) {
              handlers.forEach((handler) => {
                handler.call(node.port, ev);
              });
            };
            node.connect(this.context.destination);
          }
        });
      }
      // Ensure we never schedule in the past
      const startTime = Math.max(this.scheduledTime, this.context.currentTime);
      source.start(startTime);
      this.scheduledTime = startTime + audioBuffer.duration;
    }

    if (this.audioQueue.length === 0) {
      if (this.isStreamComplete) {
        this.isPlaying = false;
        if (this.checkInterval) {
          clearInterval(this.checkInterval);
          this.checkInterval = null;
        }
      } else {
        if (!this.checkInterval) {
          this.checkInterval = window.setInterval(() => {
            if (this.audioQueue.length > 0) {
              this.scheduleNextBuffer();
            }
          }, 100) as unknown as number;
        }
      }
    } else {
      const nextCheckTime =
        (this.scheduledTime - this.context.currentTime) * 1000;
      setTimeout(
        () => this.scheduleNextBuffer(),
        Math.max(0, nextCheckTime - 50)
      );
    }
  }

  stop() {
    this.isPlaying = false;
    this.isStreamComplete = true;
    this.audioQueue = [];
    this.scheduledTime = this.context.currentTime;

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.gainNode.gain.linearRampToValueAtTime(
      0,
      this.context.currentTime + 0.1
    );

    setTimeout(() => {
      this.gainNode.disconnect();
      this.gainNode = this.context.createGain();
      this.gainNode.connect(this.playoutSink());
    }, 200);
  }

  async resume() {
    if (this.context.state === "suspended") {
      await this.context.resume();
    }
    this.isStreamComplete = false;
    this.scheduledTime = this.context.currentTime + this.initialBufferTime;
    this.gainNode.gain.setValueAtTime(1, this.context.currentTime);
  }

  complete() {
    this.isStreamComplete = true;
    this.onComplete();
  }
}

// // Usage example:
// const audioStreamer = new AudioStreamer();
//
// // In your streaming code:
// function handleChunk(chunk: Uint8Array) {
//   audioStreamer.handleChunk(chunk);
// }
//
// // To start playing (call this in response to a user interaction)
// await audioStreamer.resume();
//
// // To stop playing
// // audioStreamer.stop();
